/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';

import { style, animate, transition, state, trigger } from '@angular/animations';

import { ActivatedRoute } from '@angular/router';

import { BsModalService } from 'ngx-bootstrap/modal';

import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { ErrorService } from '../../services/error.service';
import { EloquaService } from '../../services/eloqua.service';
import { EloquaCdoService } from '../../services/eloqua-cdo.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { AlertDialogComponent } from '../../global/alert-dialog/alert-dialog.component';
import { DriftService } from '../../services/drift.service';

import { Constants, EmailType, ContactQuality, Language, SiftrockField } from '../../global/constants';

@Component({
  selector: 'app-cdodetail',
  templateUrl: './cdo-detail.component.html',
  animations: [
    trigger('draftState',
      [
        state('inactive', style({
          opacity: 0,
          transform: 'scale(.9)'
        })),
        state('active', style({
          opacity: 1,
          transform: 'scale(1)'
        })),
        transition('inactive => active', animate('300ms ease-in'))
      ]
    )
  ]
})
export class CdoDetailComponent implements OnInit {

  loadingPage = true;
  id: string = '';
  partner: string = 'eloqua';
  activeStep: string = 'type';
  help: boolean = false;
  cdoSync: any = {};
  type: string = '';
  draftSaved: string = '';
  draftState: string = 'inactive';

  // type management
  selectedTypes: any = {};
  selectedLanguages: any[] = [];
  languageList: any[] = [];
  languageDDSettings: any = {
    text: 'Select language(s)',
    unSelectAllText: 'Remove all but English'
  };

  // list state/management
  cdos: any[] = [];
  currentCdo: string;

  // field/state management
  fields: any[] = [];
  mapCoreFields = [];
  currentSRField: SiftrockField;
  destFields: any[] = [];
  currentDestField: any;
  currentStaticField: any;
  staticValue: string = '';
  currentSelectedFields: any[] = [];
  currentSelectedMappedFields: any[] = [];

  // filter/state management
  //currentIncludeFilterField: SiftrockField;
  //includeFilterValue: string = '';
  //currentExcludeFilterField: SiftrockField;
  //excludeFilterValue: string = '';

  quality = {
    high: true,
    med: true,
    low: false
  };

  refreshState = {
    cdos: 0,
    fields: 0,
  };

  constructor(private route: ActivatedRoute,
    private cacheService: CacheService,
    private sessionService: SessionService,
    private errorService: ErrorService,
    private modalService: BsModalService,
    private cdoService: EloquaCdoService,
    private eloquaService: EloquaService,
    private globalMessageService: GlobalMessageService,
    private driftService: DriftService,
    private constants: Constants) { }

  ngOnInit() {
    this.constants.emailTypes.forEach(t => {
      this.selectedTypes[t.typeName] = false;
    });

    this.constants.languages.forEach(l => {
      if (l.supported) {
        this.languageList.push({
          id: l.name,
          itemName: l.display
        });
      }
    });

    this.languageList.push({
      id: 'other',
      itemName: 'Other'
    });

    if (!this.cacheService.get('session')) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }
  }

  typeChange() {
    this.cdoSync.trigger.type = [];
    for (var key in this.selectedTypes) {
      if (this.selectedTypes[key]) {
        this.cdoSync.trigger.type.push(key);
      }
    }

    var update = { 'trigger.type': this.cdoSync.trigger.type };
    this.saveCdoSync(update);
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Email type' });
  }

  languageAdd(item) {
    if (this.selectedLanguages.length == this.languageList.length) {
      // 'all' languages
      this.cdoSync.trigger.language = ['all'];
    }
    else {
      this.cdoSync.trigger.language = this.selectedLanguages.map(l => { return l.id });
    }
    var update = { 'trigger.language': this.cdoSync.trigger.language };
    this.saveCdoSync(update);
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Language' });
  }

  languageRemove(item) {
    if (this.selectedLanguages.length > 0) {
      this.cdoSync.trigger.language = this.selectedLanguages.map(l => { return l.id });
    }
    else {
      // force at least one language
      this.selectedLanguages = [{ id: 'en', itemName: 'English' }];
      this.cdoSync.trigger.language = ['en'];
    }
    var update = { 'trigger.language': this.cdoSync.trigger.language };
    this.saveCdoSync(update);
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Language' });
  }

  languageAddAll(items) {
    this.cdoSync.trigger.language = ['all'];
    var update = { 'trigger.language': ['all'] };
    this.saveCdoSync(update);
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Language' });
  }

  languageRemoveAll(items) {
    // force at least one language
    this.selectedLanguages = [{ id: 'en', itemName: 'English' }];
    this.cdoSync.trigger.language = ['en'];
    var update = { 'trigger.language': this.cdoSync.trigger.language };
    this.saveCdoSync(update);
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Language' });
  }

  changeQuality() {
    if (!this.quality.low && !this.quality.med && !this.quality.high) {
      this.cdoSync.trigger.confidence = [1];
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          message: `At least one contact quality must be chosen. We've automatically selected "High".`,
          confirmBtn: 'Ok'
        }
      }));

      setTimeout(() => {
        this.quality.high = true;
      }, 50);
    }
    else {
      this.cdoSync.trigger.confidence = [];
      if (this.quality.high) {
        this.cdoSync.trigger.confidence.push(1);
      }
      if (this.quality.med) {
        this.cdoSync.trigger.confidence.push(2);
      }
      if (this.quality.low) {
        this.cdoSync.trigger.confidence.push(3);
      }
    }

    this.saveCdoSync({ 'trigger.confidence': this.cdoSync.trigger.confidence });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Contact quality' });
  }

  changeTriggerRecipient() {
    this.saveCdoSync({ 'trigger.recipient': this.cdoSync.trigger.recipient });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Create recipient' });
  }

  refreshCdos() {
    this.eloquaService.clearCdoListCache();

    this.getCdoList();
  }

  changeCdo(list) {
    this.currentCdo = list.value;
    this.cdoSync.action.list = {
      id: Math.floor(list.value),
      name: list.label
    };

    this.cdoSync.action.fields = [];
    this.cdoSync.action.recipient_fields = [];
    this.cdoSync.action.static_fields = [];
    this.cdoSync.action.include_filters = [];
    this.cdoSync.action.exclude_filters = [];

    // field/state management
    this.currentSRField = null;
    this.destFields = [];
    this.currentDestField = null;
    this.currentStaticField = null;
    this.staticValue = '';
    this.currentSelectedFields = [];
    this.currentSelectedMappedFields = [];

    this.refreshFields();
    this.saveCdoSync({ 'action.list': this.cdoSync.action.list });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'List' });
  }

  clearCdo() {
    this.currentCdo = '';
    this.cdoSync.action.list = {};
    this.saveCdoSync({ 'action.list': this.cdoSync.action.list });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'List' });
  }

  refreshFields() {
    this.eloquaService.clearCdoFieldCache();
    this.getFields();
  }

  srFieldChange(field) {
    this.destFields = [];
    this.fields.forEach((f) => {
      if (f.type == field.type && !f.used) {
        this.destFields.push(f);
      }
    });
  }

  addSRFieldMapping() {
    this.cdoSync.action.fields.push({
      id: this.currentDestField.id,
      description: this.currentDestField.description,
      name: this.currentDestField.name,
      siftrock_description: this.currentSRField.display,
      value: this.currentSRField.fieldName
    });

    this.cdoSync.action.fields.sort(function (a, b) {
      if (a.description < b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.fields.forEach((f) => {
      if (f.id == this.currentDestField.id) {
        f.used = true;
      }
    });

    this.constants.siftrockFields.forEach((f) => {
      if (f.fieldName == this.currentSRField.fieldName) {
        f.used = true;
      }
    });

    this.currentSRField = null;
    this.currentDestField = null;
    this.saveCdoSync({ 'action.fields': this.cdoSync.action.fields });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Drift Email field map' });
  }

  removeSRFieldMapping(field) {
    this.currentSRField = null;
    this.currentDestField = null;
    this.cdoSync.action.fields.splice(this.cdoSync.action.fields.indexOf(field), 1);
    this.saveCdoSync({ 'action.fields': this.cdoSync.action.fields });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Drift Email field map' });
  }

  addStaticFieldMapping() {
    this.cdoSync.action.static_fields.push({
      id: this.currentStaticField.id,
      description: this.currentStaticField.description,
      name: this.currentStaticField.name,
      value: this.staticValue
    });

    this.fields.forEach((f) => {
      if (f.id == this.currentStaticField.id) {
        f.used = true;
      }
    });

    this.currentStaticField = null;
    this.staticValue = '';
    this.saveCdoSync({ 'action.static_fields': this.cdoSync.action.static_fields });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Static field map' });
  }

  removeStaticFieldMapping(field) {
    this.currentStaticField = null;
    this.staticValue = '';
    this.cdoSync.action.static_fields.splice(this.cdoSync.action.static_fields.indexOf(field), 1);
    this.saveCdoSync({ 'action.static_fields': this.cdoSync.action.static_fields });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Static field map' });
  }

  addSenderFieldMapping() {
    this.currentSelectedFields.forEach((f) => {
      this.cdoSync.action.recipient_fields.push(f);
      this.fields.forEach((f2) => {
        if (f2.id == f.id) {
          f2.used = true;
        }
      });
    });

    this.cdoSync.action.recipient_fields.sort((a, b) => {
      if (a.description > b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.currentSelectedFields = [];
    this.saveCdoSync({ 'action.recipient_fields': this.cdoSync.action.recipient_fields });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Sender field map' });
  }

  removeSenderFieldMapping() {
    this.currentSelectedMappedFields.forEach((f) => {
      this.cdoSync.action.recipient_fields.splice(this.cdoSync.action.recipient_fields.indexOf(f), 1);
      this.fields.forEach((f2) => {
        if (f.id == f2.id) {
          f2.used = false;
        }
      });
    });

    this.cdoSync.action.recipient_fields.sort((a, b) => {
      if (a.description > b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.currentSelectedMappedFields = [];
    this.saveCdoSync({ 'action.recipient_fields': this.cdoSync.action.recipient_fields });
    this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Sender field map' });
  }

  // addIncludeFilter() {
  //   this.includeFilterValue = this.includeFilterValue.toLowerCase();
  //   var found = false;
  //   this.cdoSync.action.include_filters.forEach((f) => {
  //     if (f.field == this.currentIncludeFilterField.fieldName) {
  //       found = true;
  //       f.values.push(this.includeFilterValue);
  //     }
  //   });

  //   if (!found) {
  //     this.cdoSync.action.include_filters.push({
  //       field: this.currentIncludeFilterField.fieldName,
  //       display: this.currentIncludeFilterField.display,
  //       values: [this.includeFilterValue]
  //     });
  //   }

  //   this.cdoSync.action.include_filters.sort((a, b) => {
  //     if (a.display < b.display) {
  //       return -1;
  //     }
  //     else {
  //       return 1;
  //     }
  //   });

  //   this.includeFilterValue = '';
  //   this.saveCdoSync({ 'action.include_filters': this.cdoSync.action.include_filters });
  //   this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Include filter' });
  // }

  // removeIncludeFilter(filter, value) {
  //   filter.values.splice(filter.values.indexOf(value), 1);
  //   if (filter.values.length == 0) {
  //     this.cdoSync.action.include_filters.splice(this.cdoSync.action.include_filters.indexOf(filter), 1);
  //   }
  //   this.saveCdoSync({ 'action.include_filters': this.cdoSync.action.include_filters });
  //   this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Include filter' });
  // }

  // addExcludeFilter() {
  //   this.excludeFilterValue = this.excludeFilterValue.toLowerCase();
  //   var found = false;
  //   this.cdoSync.action.exclude_filters.forEach((f) => {
  //     if (f.field == this.currentExcludeFilterField.fieldName) {
  //       found = true;
  //       f.values.push(this.excludeFilterValue);
  //     }
  //   });

  //   if (!found) {
  //     this.cdoSync.action.exclude_filters.push({
  //       field: this.currentExcludeFilterField.fieldName,
  //       display: this.currentExcludeFilterField.display,
  //       values: [this.excludeFilterValue]
  //     });
  //   }

  //   this.cdoSync.action.exclude_filters.sort((a, b) => {
  //     if (a.display < b.display) {
  //       return -1;
  //     }
  //     else {
  //       return 1;
  //     }
  //   });

  //   this.excludeFilterValue = '';
  //   this.saveCdoSync({ 'action.exclude_filters': this.cdoSync.action.exclude_filters });
  //   this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Exclude filter' });
  // }

  // removeExcludeFilter(filter, value) {
  //   filter.values.splice(filter.values.indexOf(value), 1);
  //   if (filter.values.length == 0) {
  //     this.cdoSync.action.exclude_filters.splice(this.cdoSync.action.exclude_filters.indexOf(filter), 1);
  //   }
  //   this.saveCdoSync({ 'action.exclude_filters': this.cdoSync.action.exclude_filters });
  //   this.driftService.track('edited-cdoSync', { type: `${this.type} Contacts`, field: 'Exclude filter' });
  // }

  private getCdoList() {
    this.refreshState.cdos = 1;
    this.cdos = [];

    this.eloquaService.getCdoLists().subscribe(
      (result: any) => {
        this.normalizeLists(result);
      },
      error => {
        this.refreshState.cdos = 2;
      }
    )

  }

  private normalizeLists(mapResult) {
    mapResult.forEach(l => {
      this.cdos.push({
        label: l.name,
        value: l.id.toString()
      });

      if (this.cdoSync.action.list.id && this.cdoSync.action.list.id == l.id) {
        this.currentCdo = l.id.toString();
      }
    });
    this.refreshState.cdos = 0;
    if (this.cdos.length == 0) {
      this.refreshState.cdos = 2;
    }

    this.cdos.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      else {
        return 1;
      }
    });
  }

  private getFields() {

    if (!this.currentCdo) {
      return;
    }

    this.refreshState.fields = 1;
    this.fields = [];


    this.eloquaService.getCdoFields(this.currentCdo).subscribe(
      (result: any) => {
        if (!result) {
          this.refreshState.fields = 2;
        }
        else {

          this.normalizeFields(result);
        }
      },
      error => {
        this.refreshState.fields = 2;
      }
    );
  }


  private normalizeFields(mapResult) {
    this.fields = mapResult;
    this.refreshState.fields = 0;
    this.fields.forEach((f) => {
      f.used = false;

      // // system fields are those we automatically copy or are invalid
      // this.constants.systemFields.forEach((sysf) => {
      //   if (sysf.partner == this.partner && sysf.name == f.name) {
      //     f.used = true;
      //   }
      // });

      // this.cdoSync.action.fields.forEach((af) => {
      //   if (af.id == f.id) {
      //     f.used = true;
      //   }
      // });

      // this.cdoSync.action.static_fields.forEach((sf) => {
      //   if (sf.id == f.id) {
      //     f.used = true;
      //   }
      // });

      // this.cdoSync.action.recipient_fields.forEach((rf) => {
      //   if (rf.id == f.id) {
      //     f.used = true;
      //   }
      // });
    });

    this.fields.sort((a, b) => {
      if (a.description < b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    // get core fields for display (these are grayed out at the top and defaulted so no need to map)
    // this.mapCoreFields = [];
    // this.constants.coreFields.forEach(cf => {
    //   if (cf.partner == this.partner) {
    //     this.mapCoreFields.push(cf);
    //   }
    // });
  }

  private initView() {
    this.route.params.subscribe(
      params => {
        this.partner = 'eloqua';
        this.id = params['id'];
        if (this.id == 'new') {
          // check query string for type
          this.route.queryParams.subscribe(
            qs => {
              this.initNew(qs['type'] || 'contacts');
              this.type = this.cdoSync.trigger.data == 'contacts' ? 'Create' : 'Update';
              this.getCdoList();
              this.refreshFields();
              this.loadingPage = false;
            }
          );
        }
        else {
          this.cdoService.getSync(this.id).subscribe(
            (result: any) => {
              this.cdoSync = result;
              this.cdoSync.trigger.type.forEach((t) => {
                this.selectedTypes[t] = true;
              });
              this.quality = {
                low: false,
                med: false,
                high: false
              };
              if (this.cdoSync.trigger.confidence.indexOf(1) >= 0) {
                this.quality.high = true;
              }
              if (this.cdoSync.trigger.confidence.indexOf(2) >= 0) {
                this.quality.med = true;
              }
              if (this.cdoSync.trigger.confidence.indexOf(3) >= 0) {
                this.quality.low = true;
              }
              this.type = this.cdoSync.trigger.data == 'contacts' ? 'Create' : 'Update';

              this.cdoSync.trigger.language.forEach(lang => {
                this.languageList.forEach(listlang => {
                  if (listlang.id == lang || lang == 'all') {
                    this.selectedLanguages.push(listlang);
                  }
                });
              });

              this.getCdoList();
              this.refreshFields();
              this.loadingPage = false;
            },
            error => {
              this.globalMessageService.error();
              this.errorService.log(error, 'cdo-detail.component', 'ngOnInit.100');
            }
          )
        }
      }
    )
  }



  private saveCdoSync(update: any) {
    this.draftState = 'inactive';
    if (this.cdoSync._id == 'new') {
      // special case for first-time save
      this.cdoService.saveNew(this.cdoSync).subscribe(
        (result: any) => {
          this.cdoSync = result;
          this.draftSaved = new Date().toLocaleTimeString();
          this.draftState = 'active';
          this.driftService.track('added-cdoSync', { type: `${this.type} Contacts` });
        },
        error => {
          this.globalMessageService.error();
          this.errorService.log(error, 'cdo-detail.component', 'saveCdoSync.100');
        }
      )
    }
    else {
      this.cdoService.savePartial(this.cdoSync._id, update).subscribe(
        (result: any) => {
          this.draftSaved = new Date().toLocaleTimeString();
          this.draftState = 'active';
        },
        error => {
          this.globalMessageService.error();
          this.errorService.log(error, 'cdo-detail.component', 'saveCdoSync.200');
        }
      )
    }
  }

  private initNew(dataType: string) {
    let defaultTypes: string[] = [];
    if (dataType == 'contacts') {
      defaultTypes = [
        'changed',
        'general',
        'human',
        'left',
        'vacation'
      ];

      defaultTypes.forEach((t) => {
        this.selectedTypes[t] = true;
      });

      // Default is English only for new contacts
      this.selectedLanguages = [{
        id: 'en',
        itemName: 'English'
      }];
    }
    else {
      // Default is all languages
      this.selectedLanguages = JSON.parse(JSON.stringify(this.languageList));
    }

    this.cdoSync = {
      _id: 'new',
      type: 'integration',
      partner: this.partner.toLowerCase(),
      trigger: {
        type: defaultTypes,
        data: dataType,
        confidence: [1, 2],
        language: ['all'],
        recipient: false
      },
      action: {
        list: {},
        fields: [],
        recipient_fields: [],
        static_fields: [],
        include_filters: [],
        exclude_filters: []
      },
      enabled: false,
      deleted: false
    };

    this.updateSession(dataType);
  }

  private updateSession(dataType) {
    // when a user creates a new , re-set their session to whatever type they are creating
    // so when navigating back the created one will be on the screen
    this.sessionService.getSession().subscribe(
      (result: any) => {
        if (result.cdosync_filters) {
          result.cdosync_filters.type = 'all';
          result.cdosync_filters.cdo_type = dataType;
          result.cdosync_filters.last_changed = new Date();
          this.sessionService.saveSession(result).subscribe(
            (result: any) => {
              // Nothing
            },
            err => {
              this.errorService.log(err, 'cdo-detail.component', 'saveSession');
            }
          )
        }
      },
      error => {

      }
    )
  }
}



// WEBPACK FOOTER //
// ./client/app/config/cdo/cdo-detail.component.ts